'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { DB_TYPE } from '@quesmed/types';
import { useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { IoMail } from 'react-icons/io5';
import { z } from 'zod';
import { EEnvironment } from '@/config';
import { useAuthHandler } from '@/core/hooks';
import { Button, cn, FormCheckBox, FormInput, FormPasswordInput } from '@/core/ui';
import { useGlobal } from '@/store';

// https://quesmed.com/illustrations/MRCPProductImage.svg
// https://quesmed.com/illustrations/CMProductImage.svg
// https://quesmed.com/illustrations/InterviewProductImage.svg
// https://quesmed.com/illustrations/AMSProductImage.svg
// https://images.quesmed.com/40krxfb01707144596982.jpg
// https://play-lh.googleusercontent.com/cAn5-upGTECL3kyrB92TB0AX6F1Z0gWiH68u7PoQzfcEsHbKdRZo-q6jSJOKkMqlxaM
// https://cdn.dribbble.com/userupload/12406867/file/original-00a077763cf2d386d20063a169ce2773.png
// https://study-hub.org/wp-content/uploads/2020/09/Quesmed.png

const schema = z.object({
  username: z.email('Not a Valid email').min(1, 'Username is required'),
  password: z.string().min(1, 'Password cannot be empty'),
  remember: z.boolean(),
});

type ISchema = z.infer<typeof schema>;

const Login = () => {
  const setDbType = useGlobal.use.setDbType();
  const setEnvironment = useGlobal.use.setEnvironment();
  const { login, loginLoading: loading } = useAuthHandler();
  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      remember: false,
    },
  });

  useEffect(() => {
    trigger(['password', 'remember', 'username']);
    setDbType(DB_TYPE.FINALS);
    setEnvironment(EEnvironment.PRODUCTION);
    return () => {
      setDbType(DB_TYPE.FINALS);
    };
  }, []);

  const onSubmit: SubmitHandler<ISchema> = async ({ password, remember, username }) => {
    await login({ username: username.trim(), password: password.trim(), days: remember ? 7 : 1 });
  };

  return (
    <div
      className={cn(
        'py-4 md:py-8 lg:py-12 px-4 md:px-8',
        'flex flex-1 flex-col justify-center items-center',
        'min-h-full w-full h-screen md:h-auto',
        'bg-gradient-to-r from-primary-100 via-primary-300 to-primary-300',
      )}
    >
      <div className='mx-auto h-full md:h-auto w-full container'>
        <div className='rounded-xl h-full bg-default-50 shadow-lg flex flex-col px-8 md:px-16 py-10'>
          {/* <div className='flex flex-row items-center space-x-3'>
            <Image alt='logo' width={40} height={40} src='/logo.svg' />
            <h5 className='text-foreground font-medium uppercase text-2xl'>Quesmed</h5>
          </div> */}
          <div className='grid grid-cols-1 lg:grid-cols-2 items-center'>
            <div className='lg:flex hidden col-span-1 justify-end items-end p-12'>
              <img
                alt='quesmed brain'
                width={150}
                height={150}
                className='h-auto w-auto'
                src='/assets/brain-desktop.svg'
              />
            </div>

            <div className='lg:p-10 col-span-1'>
              <div className='flex flex-row items-center space-x-3'>
                <img alt='logo' width={40} height={40} src='/logo.svg' />
                <h5 className='font-medium uppercase text-2xl'>Quesmed Admin</h5>
              </div>
              {/* <h2 className='text-default-800 text-xl md:text-2xl'>Welcome to Quesmed Admin</h2> */}
              <form
                autoComplete='off'
                onSubmit={handleSubmit(onSubmit)}
                className='flex flex-col space-y-5 mt-10'
              >
                <FormInput
                  description="We'll never share your email with anyone else."
                  control={control}
                  // autoComplete='off'
                  name='username'
                  // type='username'
                  className='rounded'
                  variant='faded'
                  label='Username'
                  endContent={
                    <IoMail className='text-2xl text-default-400 pointer-events-none flex-shrink-0' />
                  }
                />

                <FormPasswordInput
                  control={control}
                  // autoComplete='new-password'
                  description='Toggle eye to check password'
                  name='password'
                  className='rounded'
                  label='Password'
                  variant='faded'
                />

                <div className='flex flex-row items-center justify-between space-x-3'>
                  <FormCheckBox control={control} name='remember'>
                    Remember me
                  </FormCheckBox>
                  {/* <span className='text-default-600 text-sm'>
                    Don't have an account? &nbsp;
                    <Link
                      to={ROUTES.REGISTER}
                      className='text-primary-600 text-sm hover:text-primary-700 font-medium'
                    >
                      Register
                    </Link>
                  </span> */}
                </div>
                <Button color='primary' className='' isLoading={loading} isDisabled={!isValid} type='submit'>
                  Sign In
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
